import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Alert,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { dataGridHeaderSx } from '../../theme/themes';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../contexts/AuthContext';

type DeviceSetting = {
  id: string;
  deviceId: string;
  settingName: string;
  settingValue: string;
  dateCreated: string;
  archivedDate: string | null;
};

type Props = { deviceId: string };

const DeviceSettingsComponent: React.FC<Props> = ({ deviceId }) => {
  const auth = useAuth();
  const { mode } = useThemeMode();
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens]);
  const [settings, setSettings] = useState<DeviceSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<DeviceSetting | null>(null);
  const [formName, setFormName] = useState('');
  const [formValue, setFormValue] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/devices/${deviceId}/settings`, { headers: authHeader });
      if (!res.ok) throw new Error('Failed to load settings');
      const data: DeviceSetting[] = await res.json();
      setSettings(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const handleAdd = () => {
    setEditingSetting(null);
    setFormName('');
    setFormValue('');
    setDialogOpen(true);
  };

  const handleEdit = (setting: DeviceSetting) => {
    setEditingSetting(setting);
    setFormName(setting.settingName);
    setFormValue(setting.settingValue);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/devices/${deviceId}/settings/${id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!res.ok) throw new Error('Failed to delete setting');
      setSuccess('Setting deleted successfully');
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to delete setting');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingSetting(null);
    setFormName('');
    setFormValue('');
  };

  const handleDialogSave = async () => {
    setError(null);
    setSuccess(null);
    
    if (!formName.trim()) {
      setError('Setting name is required');
      return;
    }

    try {
      if (editingSetting) {
        // Update existing setting
        const res = await fetch(`/api/devices/${deviceId}/settings/${editingSetting.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({
            id: editingSetting.id,
            deviceId: deviceId,
            settingName: formName,
            settingValue: formValue,
          }),
        });
        if (!res.ok) throw new Error('Failed to update setting');
        setSuccess('Setting updated successfully');
      } else {
        // Create new setting
        const res = await fetch(`/api/devices/${deviceId}/settings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({
            settingName: formName,
            settingValue: formValue,
          }),
        });
        if (!res.ok) throw new Error('Failed to create setting');
        setSuccess('Setting created successfully');
      }
      
      handleDialogClose();
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save setting');
    }
  };

  const handleExport = () => {
    try {
      // Create a clean export object with only relevant fields
      const exportData = settings.map(setting => ({
        settingName: setting.settingName,
        settingValue: setting.settingValue,
        dateCreated: setting.dateCreated,
      }));

      // Create JSON string with pretty formatting
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create blob and download link
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with device ID and timestamp
      const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      link.download = `device-settings-${deviceId}-${timestamp}.json`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccess('Settings exported successfully');
    } catch (e: any) {
      setError(e.message || 'Failed to export settings');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      // Read file content
      const fileContent = await file.text();
      
      // Parse JSON
      let importData: any[];
      try {
        importData = JSON.parse(fileContent);
      } catch {
        throw new Error('Invalid JSON file format');
      }

      // Validate data structure
      if (!Array.isArray(importData)) {
        throw new Error('JSON must contain an array of settings');
      }

      // Validate each setting has required fields
      const validSettings = importData.filter(item => 
        item && typeof item === 'object' && item.settingName
      );

      if (validSettings.length === 0) {
        throw new Error('No valid settings found in file. Each setting must have a "settingName" field.');
      }

      // Send to API
      const res = await fetch(`/api/devices/${deviceId}/settings/import`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...authHeader 
        },
        body: JSON.stringify(validSettings),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to import settings');
      }

      const result = await res.json();
      setSuccess(
        `Import completed: ${result.created} created, ${result.updated} updated` +
        (result.errors?.length > 0 ? ` (${result.errors.length} errors)` : '')
      );
      
      if (result.errors?.length > 0) {
        console.warn('Import errors:', result.errors);
      }

      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to import settings');
    } finally {
      setLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'settingName',
      headerName: 'Setting Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'settingValue',
      headerName: 'Value',
      flex: 2,
      minWidth: 300,
    },
    {
      field: 'dateCreated',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => {
        const value = params.row.dateCreated;
        if (!value) return '';
        const date = new Date(value);
        if (isNaN(date.getTime())) return 'Invalid Date';
        // Format as yyyy-MM-dd HH:mm:ss
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params: any) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Device Settings</Typography>
        <Stack direction="row" spacing={1}>
          <input
            type="file"
            ref={fileInputRef}
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            onClick={handleExport}
            disabled={settings.length === 0}
          >
            Export JSON
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleImportClick}
            disabled={loading}
          >
            Import JSON
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Setting
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={settings}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: {
              sortModel: [{ field: 'settingName', sort: 'asc' }],
            },
          }}
          sx={dataGridHeaderSx(mode)}
          disableRowSelectionOnClick
        />
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSetting ? 'Edit Setting' : 'Add Setting'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Setting Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
              required
              disabled={!!editingSetting}
              helperText={editingSetting ? 'Setting name cannot be changed' : ''}
            />
            <TextField
              label="Setting Value"
              value={formValue}
              onChange={(e) => setFormValue(e.target.value)}
              fullWidth
              multiline
              rows={4}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceSettingsComponent;
